import type { Scope, ZeroviaClient } from "@agentry/api-client";
import type {
	AuthSession,
	Project,
	ProjectListItem,
	Role,
	SessionBootstrap,
} from "@agentry/zerovia-contracts";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useApi } from "./ApiProvider";

export type AuthState =
	| { status: "loading" }
	| { status: "anonymous"; reason?: "logged-out" }
	| { status: "authenticated"; session: AuthSession; project: SelectedProject | null };

type SelectedProject = Pick<Project, "id" | "tenantId" | "name">;

const SELECTED_PROJECT_PREFIX = "agentry:selected-project:";

type AuthContextValue = {
	state: AuthState;
	session: AuthSession | null;
	role: Role | "anonymous";
	scope: Scope | null;
	login(email: string, password: string): Promise<AuthSession>;
	signup(input: {
		email: string;
		password: string;
		name: string;
		attributes?: Record<string, string>;
	}): Promise<SessionBootstrap>;
	acceptInvite(token: string, input: { name: string; password: string }): Promise<AuthSession>;
	logout(): Promise<void>;
	setProject(projectId: string, projectName: string): void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const { client } = useApi();
	const [state, setState] = useState<AuthState>({ status: "loading" });

	useEffect(() => {
		let cancelled = false;
		setState({ status: "loading" });
		client
			.me()
			.then(async (session) => {
				if (cancelled) return;
				if (!session) {
					setState({ status: "anonymous" });
					return;
				}
				const project = await resolveProject(client, session);
				if (!cancelled) setState({ status: "authenticated", session, project });
			})
			.catch(() => {
				// Token失効・通信失敗はAnonymousへ倒す。Errorを握り潰さず、Login導線へ戻す。
				if (!cancelled) setState({ status: "anonymous" });
			});
		return () => {
			cancelled = true;
		};
	}, [client]);

	const login = useCallback(
		async (email: string, password: string) => {
			const session = await client.login({ email, password });
			const project = await resolveProject(client, session);
			setState({ status: "authenticated", session, project });
			return session;
		},
		[client],
	);

	const signup = useCallback(
		async (input: {
			email: string;
			password: string;
			name: string;
			attributes?: Record<string, string>;
		}) => {
			const { attributes, ...credentials } = input;
			const session = await client.signup(credentials);
			if (attributes && Object.keys(attributes).length > 0) {
				try {
					await client.upsertUserAttributes(session.user.id, attributes);
				} catch {
					// B160 未実装でも登録自体は成功させる。一覧は属性なしとして全表示する。
				}
			}
			persistSelectedProject(session.tenant.id, session.project.id);
			setState({ status: "authenticated", session, project: session.project });
			return session;
		},
		[client],
	);

	const acceptInvite = useCallback(
		async (token: string, input: { name: string; password: string }) => {
			const session = await client.acceptInvite(token, input);
			const project = await resolveProject(client, session);
			setState({ status: "authenticated", session, project });
			return session;
		},
		[client],
	);

	const logout = useCallback(async () => {
		await client.logout();
		setState({ status: "anonymous", reason: "logged-out" });
	}, [client]);

	const setProject = useCallback((projectId: string, projectName: string) => {
		setState((prev) => {
			if (prev.status !== "authenticated") return prev;
			persistSelectedProject(prev.session.tenant.id, projectId);
			return {
				status: "authenticated",
				session: prev.session,
				project: { id: projectId, tenantId: prev.session.tenant.id, name: projectName },
			};
		});
	}, []);

	const value = useMemo<AuthContextValue>(() => {
		const session = state.status === "authenticated" ? state.session : null;
		const project = state.status === "authenticated" ? state.project : null;
		return {
			state,
			session,
			role: session?.membership.role ?? "anonymous",
			scope: session && project ? { tenantId: session.tenant.id, projectId: project.id } : null,
			login,
			signup,
			acceptInvite,
			logout,
			setProject,
		};
	}, [state, login, signup, acceptInvite, logout, setProject]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

async function resolveProject(
	client: ZeroviaClient,
	session: AuthSession,
): Promise<SelectedProject | null> {
	const projects = await client.listOrganizationProjects(session.tenant.id);
	const rememberedId = readSelectedProject(session.tenant.id);
	const project = projects.find((item) => item.id === rememberedId) ?? selectProject(projects);
	if (project) persistSelectedProject(session.tenant.id, project.id);
	return project ? { id: project.id, tenantId: session.tenant.id, name: project.name } : null;
}

function persistSelectedProject(tenantId: string, projectId: string): void {
	try {
		localStorage.setItem(`${SELECTED_PROJECT_PREFIX}${tenantId}`, projectId);
	} catch {
		// Storage unavailable: selection remains valid for the current React session.
	}
}

function readSelectedProject(tenantId: string): string | null {
	try {
		return localStorage.getItem(`${SELECTED_PROJECT_PREFIX}${tenantId}`);
	} catch {
		return null;
	}
}

function selectProject(projects: ProjectListItem[]): ProjectListItem | null {
	const defaultProject = projects.find((project) => project.isDefault);
	if (defaultProject) return defaultProject;
	return (
		[...projects].sort((left, right) => left.createdAt.localeCompare(right.createdAt))[0] ?? null
	);
}

export function useAuth(): AuthContextValue {
	const value = useContext(AuthContext);
	if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
	return value;
}

/** 認証済み前提の画面で使う。Scopeが無ければ呼び出し側のGuardが先に弾いている。 */
export function useScope(): Scope {
	const { scope } = useAuth();
	if (!scope) throw new Error("useScope requires an authenticated session");
	return scope;
}
